import { useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';

import { apiFetch } from '../../api/client';

interface DashboardStats {
  total_businesses: number;
  total_visitors: number;
  total_scans: number;
  revenue_leads: number;
  feedbacks: number;
  page_views: number;
}

interface StatCardProps {
  label: string;
  value: number | string;
  accent: string;
}

function StatCard({ label, value, accent }: StatCardProps) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <div className="mt-3 h-1 w-12 rounded-full" style={{ backgroundColor: accent }} />
    </div>
  );
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-IN').format(value);
}

export function Dashboard() {
  const notify = useNotify();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      try {
        const data = await apiFetch<DashboardStats>('/admin/dashboard');
        if (!cancelled) {
          setStats(data);
        }
      } catch {
        if (!cancelled) {
          notify('Failed to load dashboard stats', { type: 'warning' });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [notify]);

  return (
    <div className="p-6">
      <Title title="Dashboard" />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">QRBusiness India</h1>
        <p className="mt-1 text-sm text-gray-500">Platform overview — last 30 days</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-28 animate-pulse rounded-xl border border-gray-200 bg-gray-100"
            />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <StatCard
            label="Total Businesses"
            value={formatNumber(stats.total_businesses)}
            accent="#C0392B"
          />
          <StatCard
            label="Unique Visitors"
            value={formatNumber(stats.total_visitors)}
            accent="#1A5276"
          />
          <StatCard
            label="Page Views"
            value={formatNumber(stats.page_views)}
            accent="#2874A6"
          />
          <StatCard
            label="QR Scans"
            value={formatNumber(stats.total_scans)}
            accent="#E67E22"
          />
          <StatCard
            label="Service Leads"
            value={formatNumber(stats.revenue_leads)}
            accent="#27AE60"
          />
          <StatCard
            label="Feedback Received"
            value={formatNumber(stats.feedbacks)}
            accent="#8E44AD"
          />
        </div>
      ) : (
        <p className="text-sm text-gray-500">Dashboard data is unavailable.</p>
      )}
    </div>
  );
}
